import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { testId } from '@jwp/ott-common/src/utils/common';
import { hasVideo } from '@jwp/ott-common/src/utils/sources';
import { isPlayableLiveEvent } from '@jwp/ott-common/src/utils/liveEvent';

import TruncatedText from '../TruncatedText/TruncatedText';
import StartWatchingButton from '../../containers/StartWatchingButton/StartWatchingButton';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';

import styles from './HeroShelf.module.scss';

const HeroShelfMetadata = ({
  item,
  loading,
  playlistId,
  style,
  hidden,
}: {
  item: PlaylistItem | null;
  loading: boolean;
  playlistId: string | undefined;
  style: CSSProperties;
  hidden?: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint < Breakpoint.sm;

  if (!item) return null;

  const itemHasVideo = hasVideo(item);
  const showStartWatchingButton = itemHasVideo && !isMobile && isPlayableLiveEvent(item);

  return (
    <div
      className={styles.metadata}
      data-testid={testId(`shelf-hero-metadata--${hidden ? 'hidden' : 'visible'}`)}
      style={{ ...style, visibility: hidden ? 'hidden' : undefined }}
      aria-hidden={hidden ? 'true' : undefined}
    >
      <h2 className={classNames(loading ? styles.loadingTitle : styles.title)}>{!loading && item?.title}</h2>
      {item?.synopsis ? <div>{item.synopsis as string}</div> : <TruncatedText text={item?.description} maximumLines={3} />}
      <div>
        {showStartWatchingButton && <StartWatchingButton item={item} playUrl={mediaURL({ id: item.mediaid, title: item.title, playlistId, play: true })} />}
        <Button
          label={t('common:more_info')}
          aria-label={testId('hero-cta') ?? t('common:more_info')}
          onClick={() => navigate(mediaURL({ id: item.mediaid, title: item.title, playlistId }))}
          startIcon={<Icon icon={ChevronRight} />}
          size="large"
          variant={showStartWatchingButton ? undefined : 'contained'}
          color={showStartWatchingButton ? undefined : 'primary'}
        />
      </div>
    </div>
  );
};

export default HeroShelfMetadata;
